import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  listAdminOpportunities,
  updateAdminOpportunity,
  OPPORTUNITY_STAGES,
  type Opportunity,
  type OpportunityStage,
} from "@/lib/opportunities.functions";
import { OpportunityEmailWorkspace } from "@/components/admin/OpportunityEmailWorkspace";
import { OpportunityFollowUpWorkspace } from "@/components/admin/OpportunityFollowUpWorkspace";
import { authedHeaders } from "@/lib/authed-fetch";

export const Route = createFileRoute("/admin/opportunity/$opportunityId")({
  head: () => ({
    meta: [
      { title: "Opportunity · Shugert Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: OpportunityDetailPage,
});

const inputClass = "mt-2 w-full rounded-xl border border-slate-300 bg-background px-3 py-2.5 text-sm text-foreground shadow-sm outline-none focus:border-[#1f1f1f] focus:ring-2 focus:ring-[#1f1f1f]/15";

function fromMoney(value: string): number | null {
  const parsed = Number(value.replace(/,/g, "").trim());
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) : null;
}

function money(cents: number | null, currency: string): string {
  if (cents == null) return ", ";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function dateTime(value: string | null): string {
  if (!value) return "Not recorded";
  return new Date(value).toLocaleString();
}

function OpportunityDetailPage() {
  const { opportunityId } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["admin.opportunities.list"],
    queryFn: async () => (await listAdminOpportunities({ headers: await authedHeaders() })) as { opportunities: Opportunity[] },
    staleTime: 20_000,
  });

  const opportunity = query.data?.opportunities.find((item) => item.id === opportunityId) ?? null;
  const [stage, setStage] = useState<OpportunityStage>("new");
  const [qualified, setQualified] = useState(false);
  const [probability, setProbability] = useState("10");
  const [estimatedValue, setEstimatedValue] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!opportunity) return;
    setStage(opportunity.stage);
    setQualified(opportunity.qualified);
    setProbability(String(opportunity.probability));
    setEstimatedValue(opportunity.estimated_value_cents == null ? "" : String(opportunity.estimated_value_cents / 100));
    setNotes(opportunity.notes ?? "");
  }, [opportunity?.id, opportunity?.updated_at]);

  const update = useMutation({
    mutationFn: async () => updateAdminOpportunity({
      data: {
        id: opportunityId,
        stage,
        qualified,
        probability: Math.max(0, Math.min(100, Number(probability) || 0)),
        estimated_value_cents: estimatedValue.trim() ? fromMoney(estimatedValue) : null,
        notes: notes.trim() || null,
      },
      headers: await authedHeaders(),
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin.opportunities.list"] }),
        queryClient.invalidateQueries({ queryKey: ["admin.north-star.snapshot"] }),
        queryClient.invalidateQueries({ queryKey: ["admin.opportunity.follow-up", opportunityId] }),
        queryClient.invalidateQueries({ queryKey: ["admin.opportunity.follow-up.queue"] }),
      ]);
      toast.success("Opportunity updated.");
    },
    onError: (error) => toast.error(error.message),
  });

  if (query.isPending) return <p className="text-sm text-muted-foreground">Loading Opportunity…</p>;
  if (query.isError) return <ErrorState title="Opportunity unavailable" message={query.error.message} onRetry={() => query.refetch()} />;
  if (!opportunity) return <ErrorState title="Opportunity not found" message="The requested Opportunity does not exist or is outside the current admin query." onRetry={() => query.refetch()} />;

  const quote = opportunity.source_quote;
  const displayName = opportunity.company || opportunity.full_name;

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-5">
        <div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <Link to="/admin/north-star" className="hover:text-foreground">North Star</Link>
            <span>→</span>
            <Link to="/admin/opportunities" className="hover:text-foreground">Opportunities</Link>
            <span>→</span>
            <Link to="/admin/follow-up" className="hover:text-foreground">Follow-up</Link>
          </div>
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-[#1f1f1f]">Opportunity</p>
          <h1 className="mt-2 font-display text-4xl font-semibold tracking-[-0.035em] text-foreground">{displayName}</h1>
          <p className="mt-3 text-sm text-muted-foreground">{opportunity.full_name} · <a href={`mailto:${opportunity.email}`} className="underline">{opportunity.email}</a></p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full border border-border bg-card/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em]">{opportunity.stage.replace(/_/g, " ")}</span>
          <Link to="/admin/follow-up" className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-card">Follow-up queue</Link>
          <Link to="/admin/opportunities" className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-card">All opportunities</Link>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="Estimated value" value={money(opportunity.estimated_value_cents, opportunity.currency)} />
        <Metric label="Probability" value={`${opportunity.probability}%`} />
        <Metric label="First response" value={dateTime(opportunity.first_responded_at)} />
        <Metric label="Next action" value={dateTime(opportunity.next_action_at)} />
      </section>

      <OpportunityFollowUpWorkspace opportunityId={opportunity.id} />

      <section className="rounded-2xl border border-border bg-card/30 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#1f1f1f]">Original submission</p>
            <h2 className="mt-2 font-display text-2xl font-semibold">What the prospect actually sent</h2>
          </div>
          <span className="text-xs text-muted-foreground">Submitted {dateTime(quote?.created_at ?? opportunity.created_at)}</span>
        </div>

        {quote ? (
          <div className="mt-6 space-y-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Info label="Name" value={quote.full_name} />
              <Info label="Email" value={quote.email} />
              <Info label="Phone" value={quote.phone} />
              <Info label="Company" value={quote.company} />
              <Info label="Website" value={quote.website} />
              <Info label="Shopify plan" value={quote.shopify_plan} />
              <Info label="Platform" value={quote.platform} />
              <Info label="Monthly revenue" value={quote.monthly_revenue || quote.current_revenue} />
              <Info label="Engagement" value={quote.engagement_type} />
              <Info label="Timeline" value={quote.timeline} />
              <Info label="Preferred start" value={quote.preferred_start_date} />
              <Info label="Budget" value={quote.budget} />
            </div>

            <TextBlock label="Request / scope" value={quote.scope} />
            <TextBlock label="Additional message" value={quote.message} />

            <div className="grid gap-4 md:grid-cols-2">
              <ListBlock label="Services" values={quote.services} />
              <ListBlock label="Challenges" values={quote.challenges} />
            </div>

            <div className="border-t border-border pt-5">
              <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Marketing attribution</h3>
              <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Info label="Source page" value={quote.source_page} />
                <Info label="Landing page" value={quote.landing_page} />
                <Info label="Referrer" value={quote.landing_referrer} />
                <Info label="First seen" value={quote.first_seen_at} />
                <Info label="UTM source" value={quote.utm_source} />
                <Info label="UTM medium" value={quote.utm_medium} />
                <Info label="UTM campaign" value={quote.utm_campaign} />
                <Info label="UTM content" value={quote.utm_content} />
                <Info label="GCLID" value={quote.gclid} />
                <Info label="FBCLID" value={quote.fbclid} />
                <Info label="Referral code" value={quote.referral_code} />
                <Info label="Quote status" value={quote.status} />
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-5 rounded-xl border border-border bg-background p-4 text-sm text-muted-foreground">The Opportunity exists, but no source quote could be loaded. Source: {opportunity.source_type} · {opportunity.source_id}</p>
        )}
      </section>

      <OpportunityEmailWorkspace opportunityId={opportunity.id} />

      <section className="rounded-2xl border border-border bg-card/30 p-6">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#1f1f1f]">Commercial controls</p>
        <h2 className="mt-2 font-display text-2xl font-semibold">Qualification and pipeline</h2>
        <p className="mt-2 text-sm text-muted-foreground">Follow-up ownership and next action are managed in the workspace above. These controls define qualification, value and pipeline stage.</p>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <label className="text-xs font-semibold uppercase tracking-[0.13em] text-muted-foreground">Stage
            <select value={stage} onChange={(event) => setStage(event.target.value as OpportunityStage)} className={inputClass}>
              {OPPORTUNITY_STAGES.map((item) => <option key={item} value={item}>{item.replace(/_/g, " ")}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold uppercase tracking-[0.13em] text-muted-foreground">Probability %
            <input type="number" min="0" max="100" value={probability} onChange={(event) => setProbability(event.target.value)} className={inputClass} />
          </label>
          <label className="text-xs font-semibold uppercase tracking-[0.13em] text-muted-foreground">Estimated value
            <input value={estimatedValue} onChange={(event) => setEstimatedValue(event.target.value)} className={inputClass} />
          </label>
        </div>
        <label className="mt-4 flex items-center gap-3 rounded-xl border border-slate-300 bg-background p-4 text-sm shadow-sm">
          <input type="checkbox" checked={qualified} onChange={(event) => setQualified(event.target.checked)} />
          Qualified for Shugert
        </label>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-[0.13em] text-muted-foreground">Internal notes
          <textarea rows={5} value={notes} onChange={(event) => setNotes(event.target.value)} className={`${inputClass} resize-y normal-case tracking-normal`} />
        </label>
        <button type="button" disabled={update.isPending} onClick={() => update.mutate()} className="mt-5 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background disabled:opacity-50">
          {update.isPending ? "Saving…" : "Save Opportunity"}
        </button>
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return <article className="rounded-2xl border border-border bg-card/30 p-5"><p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</p><p className="mt-2 text-lg font-semibold text-foreground">{value}</p></article>;
}

function Info({ label, value }: { label: string; value: string | null | undefined }) {
  return <div className="rounded-xl border border-border bg-background p-4"><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p><p className="mt-2 break-words text-sm text-foreground">{value || "Not provided"}</p></div>;
}

function TextBlock({ label, value }: { label: string; value: string | null | undefined }) {
  return <div className="rounded-xl border border-border bg-background p-5"><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p><p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-foreground">{value || "Not provided"}</p></div>;
}

function ListBlock({ label, values }: { label: string; values: string[] | null | undefined }) {
  return <div className="rounded-xl border border-border bg-background p-5"><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p><p className="mt-3 text-sm text-foreground">{values?.length ? values.join(", ") : "Not provided"}</p></div>;
}

function ErrorState({ title, message, onRetry }: { title: string; message: string; onRetry: () => void }) {
  return <section className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6"><h1 className="font-display text-2xl font-semibold text-foreground">{title}</h1><p className="mt-3 text-sm text-destructive">{message}</p><div className="mt-5 flex gap-3"><button onClick={onRetry} className="rounded-full bg-foreground px-5 py-2 text-sm font-semibold text-background">Retry</button><Link to="/admin/opportunities" className="rounded-full border border-border px-5 py-2 text-sm font-semibold">All opportunities</Link></div></section>;
}
